<template>
    <div class="fs-3 lh-20">
        <preloader :display="loading" />
        <card
            :borderColor="newCreditCardRequired ? 'alert-50' : 'gray-40'"
            class="jc-space card-manager p-4 flex-row"
            style="align-items: center"
        >
            <div class="flex-4 al-center">
                <span class="bold">{{ $l10n.__('Moyen de paiement') }}</span>
                <div v-if="hasPaymentSetup" class="flex-row" style="align-items: center">
                    <SaasIcon :icon="icon" :width="24" :height="16" />
                    <el-tooltip :content="fullName" popperClass="w-320">
                        <span class="ml-2">
                            {{ $filters.capitalize(currentCreditCard.brand) }} ***-{{
                                currentCreditCard.last4
                            }}
                            &mdash; {{ fullNameHtmlTruncated }}
                        </span>
                    </el-tooltip>
                    <span v-if="newCreditCardRequired" class="ml-3 alert-50 semibold">
                        {{ expirationMessage }}
                    </span>
                </div>
                <div v-else>
                    <span class="alert-50 semibold">
                        {{ $l10n.__('Aucun moyen de paiement renseigné') }}
                    </span>
                </div>
                <span v-if="nextPaymentDate" :class="{ 'ml-5': hasPaymentSetup }" class="gray-60">
                    {{ $l10n.__('Prochain paiement le ') }} {{ formatNextPaymentDate }}
                </span>
            </div>
            <div v-if="hasPaymentSetup && !newCreditCardRequired">
                <CompassButton
                    :label="$t.__('common.actions.edit')"
                    type="tertiary"
                    @click="openPopupPaymentManagerCard('edit')"
                />
            </div>
            <CompassButton
                v-else
                :label="$t.__('common.actions.add_bank_card')"
                @click="openPopupPaymentManagerCard('add')"
            />
        </card>
    </div>
</template>

<script>
import Card from 'vcomponents/Card.vue';
import SaasIcon from 'vcomponents/common/SaasIcon.vue';

import { createNamespacedHelpers } from 'vuex';
import SAMMixins from 'mixins/subscription/SAMMixins';
import Preloader from 'vcomponents/utilities/Preloader.vue';

import { truncateHTML } from 'js/vue/helpers/truncateHTML.js';

const corpMapState = createNamespacedHelpers('corp').mapState;
const billingContactMapState = createNamespacedHelpers('subscriptionManager').mapState;
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;

export default {
    mixins: [SAMMixins],
    data() {
        return {
            loading: true,
            icon: '',
            currentCreditCard: {},
            displayModal: false,
        };
    },
    created() {
        this.fetchCurrentCreditCard();
        EventBus.on('payment:cardSaved', this.onCardSaved);
    },
    unmounted() {
        EventBus.off('payment:cardSaved', this.onCardSaved);
    },
    methods: {
        ...popupPanelMapActions(['open']),
        fetchCurrentCreditCard() {
            this.loading = true;
            this.getActiveCreditCard(this.id)
                .then((response) => {
                    this.currentCreditCard = response;
                    const cardType = response.brand.toLowerCase().replace(' ', '-');
                    switch (cardType) {
                        case 'mastercard':
                            this.icon = 'cardMastercard';
                            break;
                        case 'american-express':
                            this.icon = 'cardAmericanExpress';
                            break;
                        case 'diners-club':
                            this.icon = 'cardDinersClub';
                            break;
                        case 'discover':
                            this.icon = 'cardDiscover';
                            break;
                        case 'jcb':
                            this.icon = 'cardJCB';
                            break;
                        case 'unionpay':
                            this.icon = 'cardUnionPay';
                            break;
                        case 'visa':
                            this.icon = 'cardVisa';
                            break;
                        default:
                            this.icon = 'svgCard';
                    }
                })
                .catch((error) => {
                    if (error.code !== 404) {
                        Push.error(
                            this.$l10n.__(
                                'Une erreur est survenue lors de la récupération de vos informations de paiement',
                            ),
                        );
                    }
                    //If we received a 404 error we can know that creditCard not set so we open popup
                    else if (this.$route.query.from === 'informationHandler') {
                        this.openPopupPaymentManagerCard('add');
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        onCardSaved() {
            Push.success(this.$l10n.__('Votre carte bancaire a bien été mise à jour'));
            this.fetchCurrentCreditCard();
        },
        openPopupPaymentManagerCard(mode) {
            this.open({
                key: 'popupManagerPaymentCard',
                props: {
                    mode: mode,
                },
            });
        },
    },
    computed: {
        ...corpMapState(['id']),
        ...billingContactMapState(['billingContact', 'nextPaymentDate']),
        fullName() {
            return this.currentCreditCard.name;
        },
        hasPaymentSetup() {
            return this.currentCreditCard && !_.isEmpty(this.currentCreditCard);
        },
        formatNextPaymentDate() {
            return moment(this.nextPaymentDate).format('LL');
        },
        newCreditCardRequired() {
            return this.currentCreditCard.isExpired || this.currentCreditCard.isExpiring;
        },
        expirationMessage() {
            return this.currentCreditCard.isExpiring
                ? this.$l10n.__('Expire bientôt')
                : this.$l10n.__('Expirée');
        },
        fullNameHtmlTruncated() {
            return truncateHTML(this.fullName, 88);
        },
    },
    components: {
        SaasIcon,
        Card,
        Preloader,
    },
};
</script>

<style lang="scss">
.card-manager {
    .el-button.el-button--text {
        padding: 0;
    }
}
</style>
